import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { userSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import style from "./user.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { SingleSelectInput } from "../../components/SelectInput";
import { getErrorMessage } from "../../utils/common";
import {
  useCreateUserAdminMutation,
  useGetHealthOrganisationsQuery,
  useGetUserRolesQuery,
  useGetLaboratoriesQuery,
} from "../../services/api";
import { adminPrefix } from "../../utils/constants";
import {
  isLabOrCourierRole,
  isSuperAdminRole,
} from "./userShared";

/** Form values inferred from the user schema. */
type FormState = typeof userSchema.__outputType;

/** Option item used by select inputs on the Add User form. */
type SelectItem = {
  id: number;
  label: string;
  value: string;
};

/** Payload shape for the create-user mutation. */
type CreateUserPayload = {
  user_role_id?: number;
  health_org_id?: number;
  laboratory_id?: number;
  prefix: string;
  phone: string;
  mail: string;
  given_name: string;
  surname: string;
};

/**
 * Maps API list items that include id/name fields into select input options.
 *
 * Args:
 *   items: Optional list of items with id/name fields.
 *
 * Returns:
 *   SelectItem[]: Filtered list of select options with non-empty labels.
 */
const toSelectItems = <T extends { id?: number; name?: string }>(
  items?: T[]
): SelectItem[] =>
  (items ?? [])
    .map((item) => ({
      id: item?.id ?? 0,
      label: item?.name ?? "",
      value: item?.id != null ? String(item.id) : "",
    }))
    .filter((item): item is SelectItem => item.id !== 0 && item.label !== "");

/**
 * Renders the Add User form and submits user creation requests.
 *
 * Returns:
 *   JSX.Element: Add User page content.
 */
const AddUser = () => {
  const navigation = useNavigate();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: healthOrgData, isSuccess: healthOrgSuccess } =
    useGetHealthOrganisationsQuery("");
  const { data: userRoleList, isSuccess: userRoleListSuccess } =
    useGetUserRolesQuery({ is_active: true });
  const { data: laboratoryData, isSuccess: laboratoryListSuccess } =
    useGetLaboratoriesQuery({ is_active: true });

  const [createUser] = useCreateUserAdminMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const healthOrganisations = useMemo(
    () =>
      healthOrgSuccess
        ? toSelectItems<HealthOrganisation>(healthOrgData?.items)
        : [],
    [healthOrgData?.items, healthOrgSuccess]
  );

  const role = useMemo(
    () =>
      userRoleListSuccess
        ? toSelectItems<UserRole>(userRoleList?.items).filter(
            (roleItem) => roleItem.label !== "Doctor"
          )
        : [],
    [userRoleList?.items, userRoleListSuccess]
  );

  const laboratories = useMemo(
    () =>
      laboratoryListSuccess
        ? toSelectItems<Laboratory>(laboratoryData?.items)
        : [],
    [laboratoryData?.items, laboratoryListSuccess]
  );

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(userSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormState>({
    resolver: yupResolver(userSchema),
    mode: "onChange",
  });

  const selectedRoleId = watch("user_role_id");
  const selectedRole = role.find(
    (roleItem) => String(roleItem.id) === String(selectedRoleId ?? "")
  );
  const selectedRoleLabel = selectedRole?.label ?? "";
  // Role labels drive institutional routing; this assumes labels remain stable.
  const isLabSelected = isLabOrCourierRole(selectedRoleLabel);
  // Super admins bypass institution selection and require explicit flagging.
  const isSuperAdmin = isSuperAdminRole(selectedRoleLabel);

  // Keep derived is_super_admin flag in sync for validation logic.
  useEffect(() => {
    setValue("is_super_admin", isSuperAdmin);
  }, [isSuperAdmin, setValue]);

  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const formValues = { ...values };
      // Institution ids are stored in a shared field; map it to the correct payload key.
      const selectedInstitution = isLabSelected
        ? laboratories.find(
            (option) => String(option.id) === String(formValues.institution_id)
          )
        : healthOrganisations.find(
            (option) => String(option.id) === String(formValues.institution_id)
          );
      const payload: CreateUserPayload = {
        prefix: formValues?.prefix ?? "",
        phone: formValues?.phone ?? "",
        mail: formValues?.mail ?? "",
        given_name: formValues?.given_name ?? "",
        surname: formValues?.surname ?? "",
      };
      payload.user_role_id =
        formValues.user_role_id !== undefined
          ? Number(formValues.user_role_id)
          : undefined;
      // Only one of health_org_id or laboratory_id should be sent based on role type.
      payload.health_org_id = isLabSelected
        ? undefined
        : selectedInstitution?.id !== undefined
        ? Number(selectedInstitution.id)
        : undefined;
      payload.laboratory_id = isLabSelected
        ? selectedInstitution?.id != null
          ? Number(selectedInstitution.id)
          : undefined
        : undefined;
      await createUser(payload).unwrap();
      setIsLoading(false);
      setMessage({
        type: "success",
        text: "User added successfully.",
      });
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      // Delay navigation to allow the success message to remain visible.
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
      setIsLoading(false);
    }
  };

  // Reset institution when role changes to avoid stale ids from the prior list.
  useEffect(() => {
    setValue("institution_id", "");
  }, [setValue, selectedRoleId]);

  useEffect(() => {
    return () => {
      // Prevent delayed navigation from firing after unmount.
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading2}>User Details</Typography>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select user role"
              label="User Role"
              required
              register={register}
              name="user_role_id"
              errors={errors}
              data={role ?? []}
            />
            {!isSuperAdmin && (
              <SingleSelectInput
                placeholder="Select Institution"
                label="Institution Name"
                required
                value={watch("institution_id")}
                register={register}
                data={
                  !selectedRole
                    ? []
                    : isLabOrCourierRole(selectedRoleLabel)
                    ? laboratories
                    : healthOrganisations ?? []
                }
                name="institution_id"
                errors={errors}
              />
            )}
          </Box>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select Prefix"
              label="Select Prefix"
              register={register}
              name="prefix"
              errors={errors}
              data={adminPrefix ?? []}
              value={watch("prefix") ?? ""}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Enter first name"
              label="First Name"
              required
              register={register}
              name="given_name"
              errors={errors}
            />
            <Input
              placeholder="Enter last name"
              label="Last Name"
              required
              register={register}
              name="surname"
              errors={errors}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Enter email address"
              label="Email Address"
              required
              register={register}
              name="mail"
              errors={errors}
            />
            <Input
              placeholder="Enter phone number"
              label="Phone Number"
              required={isSuperAdmin}
              register={register}
              name="phone"
              errors={errors}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddUser;
